import { Button } from '@/atoms/Button'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsP, CaptionMD, EyebrowLG, EyebrowSM, Footer, HeadingSM, ParagraphXL } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext'
import { useExperimentContext } from '@/experimentation/hooks'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal'
import { AngelFundingLink } from '@/molecules/InvestLink/AngelFundingLink'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export const AngelInvestmentBanner = () => {
  const { t } = useTranslate('common')
  const { getFeatureValue } = useExperimentContext()
  const isAngelInvestmentBannerEnabled = getFeatureValue('angel_investment_banner', false)

  if (!isAngelInvestmentBannerEnabled) return null

  const leavingMessage = t('youAreLeavingToRegAAngel', 'You are now leaving Angel, Inc. to view a Reg A+ offering')
  const leavingContinueLabel = t('continueToOffering', 'Continue to Offering')
  const ARImage = 648 / 456
  const responsiveAR = {
    xs: 0,
    sm: 0,
    md: ARImage,
    lg: ARImage,
    xl: ARImage,
    '2xl': ARImage,
    '3xl': ARImage,
  }

  return (
    <BeforeYouLeaveProvider>
      <BeforeYouLeaveModal />
      <div>
        <div className="relative bg-angel-investment-banner bg-cover bg-center bg-no-repeat">
          <ResponsiveBackgroundImage
            src="/v1754673225/angel-studios/landing/angel-banner-graphic-left"
            backgroundSize="bg-contain"
            responsiveAR={responsiveAR}
            className="hidden w-5/12 max-w-[648px] bg-left-bottom sm:hidden"
          />
          <ResponsiveBackgroundImage
            src="/v1754673239/angel-studios/landing/angel-banner-graphic-right"
            backgroundSize="bg-contain"
            responsiveAR={responsiveAR}
            className="ml-auto hidden w-5/12 max-w-[648px] bg-right-bottom sm:hidden"
          />

          <div className="relative mx-4 mb-10 flex flex-col items-center justify-center pt-[88px] text-center text-black md:mb-28 md:pt-16">
            <EyebrowLG className="mb-1.5 uppercase">{t('investIn', 'Invest in')}</EyebrowLG>
            <AngelLogo color="black" className="mb-1.5 w-[196px]" />
            <EyebrowLG className="mb-8 uppercase">{t('today', 'today')}</EyebrowLG>

            <EyebrowSM className="mb-1">{t('investmentOpportunity', 'Investment Opportunity')}</EyebrowSM>
            <HeadingSM weight="bold" className="md:photon-heading-lg mb-3 text-balance">
              {t('buildTheFutureOfEntertainmentWithUs', 'Build the Future of Entertainment with Us!')}
            </HeadingSM>
            <ParagraphXL className="mb-3">
              {t('supportAngelMission', 'Support Angel’s mission to amplify light and share in its success.')}
            </ParagraphXL>
            <CaptionMD color="core-gray-700" italic className="mb-10">
              {t('availableForALimitedTime', 'Available for a limited time')}
            </CaptionMD>

            <AngelFundingLink
              href={paths.external.investmentFund.angel3}
              linkContext="Investment Banner"
              leavingMessage={leavingMessage}
              leavingContinueLabel={leavingContinueLabel}
              className="w-auto"
            >
              <Button size="lg" variant="oxide">
                {t('investNow', 'Invest Now')}
                <ExternalLinkIcon size={28} color="white" className="ml-1.5" />
              </Button>
            </AngelFundingLink>
          </div>

          <Image
            src="/v1754673213/angel-studios/landing/invest-in-angel_graphic-2"
            width={856}
            height={426}
            alt="Angel Characters"
            className="mx-auto md:hidden"
          />

          <Footer color="black" as={AsP} className="relative hidden p-4 md:block lg:px-20">
            <Translate i18nKey="angel3DisclaimerUpdated" t={t}>
              Angel Studios, Inc. is conducting an offering of securities under Regulation A. Investing in this offering
              is high-risk and Angel Studios, Inc. does NOT guarantee performance of the investment. You should not
              invest unless you can afford to lose the full amount invested. A description of the terms of the offering
              and your investment, can be found in the subscription agreement available at{' '}
              <a target="_blank" href="https://investinangel.com">
                investinangel.com
              </a>
              . You should not invest until you have reviewed and understood the offering documents, including the risk
              factors contained in the offering circular.
            </Translate>
          </Footer>
        </div>

        <Footer as={AsP} className="p-4 md:hidden">
          <Translate i18nKey="angel3DisclaimerUpdated" t={t}>
            Angel Studios, Inc. is conducting an offering of securities under Regulation A. Investing in this offering
            is high-risk and Angel Studios, Inc. does NOT guarantee performance of the investment. You should not invest
            unless you can afford to lose the full amount invested. A description of the terms of the offering and your
            investment, can be found in the subscription agreement available at{' '}
            <a target="_blank" href="https://investinangel.com">
              investinangel.com
            </a>
            . You should not invest until you have reviewed and understood the offering documents, including the risk
            factors contained in the offering circular.
          </Translate>
        </Footer>
      </div>
    </BeforeYouLeaveProvider>
  )
}
